import { Clock } from "lucide-react";
import {
  formatEventDateTime,
  formatEventTime,
  formatEventDateRange,
  formatEventDate,
  tzShortName,
  tzOffsetLabel,
  getBrowserTz,
  tzsEqualAt,
} from "@/lib/datetime";

interface EventDateTimeProps {
  /** UTC ISO start */
  start: string;
  /** UTC ISO end (optional) */
  end?: string;
  /** IANA timezone of the event location (omit for browser-local) */
  tz?: string | null;
  /** Display variant */
  variant?: "datetime" | "date-range" | "date" | "time-range";
  /** Inline (single line) vs stacked (two lines with "your time") */
  layout?: "inline" | "stacked";
  /** Hide the "your time" hint even on stacked layout */
  hideYourTime?: boolean;
  /** Show a clock icon prefix */
  showIcon?: boolean;
  /** Extra classes for the primary line */
  className?: string;
}

/**
 * Renders an event time in the event's local timezone with a small "your time"
 * hint when the viewer is in a different zone. Used everywhere event times
 * appear on the site so behaviour stays consistent.
 */
export const EventDateTime = ({
  start,
  end,
  tz,
  variant = "datetime",
  layout = "stacked",
  hideYourTime = false,
  showIcon = false,
  className = "",
}: EventDateTimeProps) => {
  if (!start) return null;
  const browserTz = getBrowserTz();
  const eventTz = tz || browserTz;
  const sameZone =
    !tz ||
    tz === browserTz ||
    tzsEqualAt(new Date(start), tz, browserTz);

  const renderPrimary = (zone: string | null | undefined) => {
    switch (variant) {
      case "date":
        return formatEventDate(start, zone);
      case "date-range":
        return formatEventDateRange(start, end, zone);
      case "time-range":
        return end
          ? `${formatEventTime(start, zone)} – ${formatEventTime(end, zone)}`
          : formatEventTime(start, zone);
      case "datetime":
      default:
        return formatEventDateTime(start, zone);
    }
  };

  const tzBadge = tz ? (() => {
    const shortName = tzShortName(start, tz);
    const offsetLabel = tzOffsetLabel(start, tz);
    return (
      <span className="text-[0.85em] text-muted-foreground ml-1.5 whitespace-nowrap">
        {shortName}
        {shortName !== offsetLabel && (
          <span className="opacity-70"> ({offsetLabel})</span>
        )}
      </span>
    );
  })() : null;

  if (layout === "inline" || sameZone || hideYourTime) {
    return (
      <span className={`inline-flex items-baseline gap-1 ${className}`}>
        {showIcon && <Clock className="h-3.5 w-3.5 shrink-0 self-center text-muted-foreground" />}
        <span>{renderPrimary(eventTz)}</span>
        {!sameZone && tzBadge}
      </span>
    );
  }

  return (
    <span className={`inline-flex flex-col ${className}`}>
      <span className="inline-flex items-baseline gap-1">
        {showIcon && <Clock className="h-3.5 w-3.5 shrink-0 self-center text-muted-foreground" />}
        <span>{renderPrimary(eventTz)}</span>
        {tzBadge}
      </span>
      <span className="text-[0.85em] text-muted-foreground">
        Your time: {renderPrimary(browserTz)}
      </span>
    </span>
  );
};
